

import { Component, Vue, Prop, InjectReactive } from "vue-property-decorator"
import { namespace, Getter } from "vuex-class"
import moment from "moment"

import vSelect from "vue-select"
import DataSourceSelect from "../Fields/DataSourceSelect.vue"
import DataSourceTemplate from "../Fields/DataSourceTemplate.vue"
import DateTime from "../Fields/DateTime.vue"
import Tags from "../Fields/Tags.vue"
import Numeric from "../Fields/Numeric.vue"
import UserRole from "./Users/UserRole.vue"
import FormGroup from "../Fields/FormGroup.vue"
import ExternalLink from "../Fields/ExternalLink.vue"
import UniqueCaseNumber from "../Fields/CaseNumber.vue"

import CaseFileUpload from "./Files/CaseFileUpload.vue"
import CaseFileDisplay from "./Files/CaseFileDisplay.vue"
import DataDetailsModal from "@/components/CaseDetails/DataDetailsModal.vue"
import MapLocation from "@/components/Fields/MapLocation.vue"
import DateTimezoneInfo from "@/components/Fields/DateTimezoneInfo.vue"

import { CaseDetails, Team, Field, CaseField, FieldInstance, CalculationType, CalculationDataTypeValue, DataType, Permission, TemplateDetails } from "@/types"

import CaseFile from "@/types/caseFile"
import { CurrentUser } from "@/types/user"

import CaseFieldValidation from "@/scripts/validation/caseFieldValidation"

import api from "@/services/api"

import { LayoutLocations } from "@/types/templateLayout"
import TimeSpentDisplay from "./TimeSpentDisplay.vue"
import HubCaseService from "@/services/hubCaseService"
import TextAreaToggleExpand from "@/components/Fields/TextAreaToggleExpand.vue"
import { ui } from "@/types/ui"
const caseStore = namespace("cases")

@Component({
	components: {
		"v-select": vSelect,
		"text-area-expand": TextAreaToggleExpand,
		"file-upload": CaseFileUpload,
		"case-file": CaseFileDisplay,
		"data-select": DataSourceSelect,
		"data-template": DataSourceTemplate,
		"tags": Tags,
		"data-details": DataDetailsModal,
		"form-group": FormGroup,
		"numeric": Numeric,
		"date-time": DateTime,
		"map-location": MapLocation,
		"user-role": UserRole,
		"external-link": ExternalLink,
		"case-number": UniqueCaseNumber,
		"time-spent-display": TimeSpentDisplay,
		"timezone-info": DateTimezoneInfo
	}
})
export default class CaseFields extends Vue {
	$refs!: {
		viewItemDetails: DataDetailsModal;
	}
	@InjectReactive("hubCaseService") hubCaseService!: HubCaseService;

	@Getter("getMe")
	public me!: CurrentUser;

	@Getter("getMyId")
	public myId!: string;

	@caseStore.Getter("getSeverities")
	public severities!: any[];

	@Prop({ default: () => [] })
	public caseFiles!: CaseFile[];

	@caseStore.Getter("getPendingUploadFiles")
	pendingUploadFiles!: any[];

	@caseStore.Mutation
	cascadeListChange!: (args: { caseDetails: CaseDetails, caseField: CaseField, newValue: any, getFields: boolean }) => void

	public dataSourceOptions: any[] = [];

	@Getter("getTeamsById")
	public teamsById!: { [key: string]: Team };

	@Prop({ default: null })
	public layout!: LayoutLocations | null;

	@Prop({ default: false })
	public canEditCurrentCase!: boolean

	@Prop()
	public currentCase!: CaseDetails;

	@Prop({ default: true })
	canUploadFiles!: boolean

	@Prop()
	currentCaseTemplate!: TemplateDetails

	ui = ui

	postInProgressDebounces: { [key: string]: NodeJS.Timeout } = {}
	savingFields: { [key: string]: boolean } = {}
	waitingFields: { [key: string]: boolean } = {}
	fieldErrors: { [key: string]: {
		error: string,
		clearTimeout: NodeJS.Timeout
	} } = {}

	public created () {
	}

	CalculationType = CalculationType

	public get sections (): string[] {
		if (!this.currentCase || !this.currentCase.sections) {
			return []
		}

		if (this.currentCase.sectionDetails && this.layout !== null) {
			return this.currentCase.sections.filter(s => this.currentCase!.sectionDetails && this.currentCase!.sectionDetails[s]
				? this.layout === this.currentCase!.sectionDetails[s].layout
				: this.layout === LayoutLocations.Default)
		} else if (!this.currentCase.sectionDetails &&
			!(this.layout === LayoutLocations.Default || this.layout === null)) {
			return []
		}

		return this.currentCase.sections
	}

	public get viewableSections () {
		if (!this.currentCase || !this.currentCase.fields) {
			return this.sections
		}

		return this.sections.filter(section => this.caseFields[section] && this.caseFields[section].some(f => this.hasViewPermission(f)))
	}

	public get caseFields (): { [key:string]: CaseField[] } {
		if (!this.currentCase) {
			return {}
		}

		const allFields: CaseField[] = []

		if (this.currentCase.fields) {
			Object.keys(this.currentCase.fields).forEach(fieldId => {
				allFields.push(this.currentCase!.fields[fieldId])
			})
		}

		allFields.forEach((field: CaseField) => {
			if (this.currentCase!.sections) {
				const sectionOrder = this.currentCase!.sections.indexOf(field.section)
				field.sectionOrder = sectionOrder === -1 ? 99999 : sectionOrder
			}
		})

		const caseFields = allFields.sortByThen({ key: "sectionOrder" }, { thenKey: "order" })

		const dataSourceFields = caseFields.filter((field: any) => field.definitionId)
			.map((field: any) => field.definitionId)

		return caseFields.reduce((group: any, field: CaseField) => {
			var section = field.section ? field.section : "General";
			(group[section] = group[section] || []).push(field)
			return group
		}, {})
	}

	private get inProgressEnabled (): boolean {
		return !!this.currentCase.id && this.me && !!this.me.enabledFeatures && !!this.me.enabledFeatures.includes("inprogresssaving")
	}

	private async fieldGotFocus (caseField: CaseField, event: FocusEvent) {
		try {
			if (this.currentCase!.id && this.hasPermission(caseField)) {
				Vue.set(this.waitingFields, caseField.field.id!, true)
				const result = await api.selectCaseField(this.currentCase!.id, caseField.field.id!, true)

				if (!result.success) {
					window.blur()

					this.fieldSetError(caseField.field.id!, `Unable to edit ${caseField.field.title} - In use by ${result.inUseByName}`)
				}
			}
		} catch (ex) {
			this.fieldSetError(caseField.field.id!, `Unable to edit ${caseField.field.title} - ${ex}`)

			window.blur()
			console.error(ex)
		} finally {
			Vue.delete(this.waitingFields, caseField.field.id!)
		}
	}

	private async fieldLostFocus (caseField: CaseField, newValue: string): Promise<void> {
		if (this.currentCase!.id && this.hasPermission(caseField)) {
			if (caseField.oldValue !== newValue) {
				await this.savefieldChange(caseField, newValue)
			} else {
				await api.selectCaseField(this.currentCase!.id, caseField.field.id!, false)
			}
		}

		if (this.savingFields[caseField.field!.id!]) {
			Vue.delete(this.savingFields, caseField.field!.id!)
		}
	}

	private fieldSetError (fieldId: string, error: string) {
		if (this.fieldErrors[fieldId]) {
			clearTimeout(this.fieldErrors[fieldId].clearTimeout)
		}

		Vue.set(this.fieldErrors, fieldId, {
			error,
			clearTimeout: setTimeout(() => {
				Vue.delete(this.fieldErrors, fieldId)
			}, 5000)
		})
	}

	private async fieldSaveCurrentValue (caseField: CaseField) {
		if (this.currentCase!.id && this.hasPermission(caseField)) {
			await this.savefieldChange(caseField, caseField.value)
		}
	}

	private async dataListItemChanged (caseField: CaseField, newValue: any, getFields: boolean = false) {
		if (!this.currentCase.id) {
			caseField.label = newValue ? newValue.label : null
		}

		if (caseField.field.dataType !== "list" && caseField.field.dataType !== "list-multiple") return

		this.cascadeListChange({
			caseDetails: this.currentCase,
			caseField,
			newValue,
			getFields: false
		})
	}

	private async dateTimeFieldLostFocus (caseField: CaseField, newValue: string): Promise<void> {
		if (this.currentCase!.id && this.hasPermission(caseField)) {
			if ((!caseField.oldValue && !!newValue) ||
			(caseField.oldValue && moment(caseField.oldValue!).isSame(newValue) === false)) {
				await this.savefieldChange(caseField, newValue)
			} else {
				await api.selectCaseField(this.currentCase!.id, caseField.field.id!, false)
			}
		} else if (!this.currentCase!.id) {
			Vue.set(this.currentCase.fields[caseField.field.id!], "value", newValue)
		}
	}

	private async savefieldChange (caseField: CaseField, newValue: string): Promise<void> {
		Vue.set(this.savingFields, caseField.field!.id!, true)
		try {
			caseField.value = newValue
			await api.saveCaseField(this.currentCase!.id!, caseField.field.id!, caseField.value)
			caseField.oldValue = newValue
		} catch (err) {
			console.error(err)
			this.fieldSetError(caseField.field.id!, "Save failed")
		} finally {
			Vue.delete(this.savingFields, caseField.field!.id!)
		}
	}

	private async addItem (caseField: CaseField, newValue: any): Promise<void> {
		if (!this.hasPermission(caseField)) {
			return
		}

		if (this.currentCase!.id) {
			api.updateCaseDataField(this.currentCase!.id, caseField.field.id!, 1, newValue)
		} else {
			if (!caseField.value) {
				Vue.set(caseField, "value", [])
			}

			caseField.value.push(newValue)
			caseField.label = caseField.value.map((i: any) => i.label).join(", ")
		}
	}
	private async updateItem (caseField: CaseField, updatedValue: any): Promise<void> {
		if (!this.hasPermission(caseField)) {
			return
		}

		if (this.currentCase!.id) {
			api.updateCaseDataField(this.currentCase!.id, caseField.field.id!, 3, updatedValue)
		} else {
			if (caseField.value) {
				caseField.value.removeWhere((item: any) => item.id === updatedValue.id)
				caseField.value.push(updatedValue)

				caseField.label = caseField.value.map((i: any) => i.label).join(", ")
			}
		}
	}
	private async removeItem (caseField: CaseField, newValue: any): Promise<void> {
		if (!this.hasPermission(caseField)) {
			return
		}

		if (this.currentCase!.id) {
			api.updateCaseDataField(this.currentCase!.id, caseField.field.id!, 2, newValue)
		} else {
			if (caseField.value) {
				caseField.value.removeWhere((item: any) => item.id === newValue.id)
				caseField.label = caseField.value.map((i: any) => i.label).join(", ")
			}
		}
	}

	private fieldFiles (fieldId: string): CaseFile[] {
		return this.caseFiles.filter(caseFile => caseFile.fieldId === fieldId)
	}

	private pendingUploadFieldFiles (fieldId: string): CaseFile[] {
		return this.pendingUploadFiles.filter(caseFile => caseFile.fieldId === fieldId)
	}

	private getPermissionTeams (field: CaseField, permissionType: keyof Permission): any[] {
		const sectionPermissions = this.currentCase!.sectionDetails &&
			this.currentCase!.sectionDetails[field.section] &&
			this.currentCase!.sectionDetails[field.section].permissions
			? this.currentCase!.sectionDetails[field.section].permissions
			: null

		if (sectionPermissions) {
			const caseSectionPermissions = this.currentCase!.isCase ? sectionPermissions.case : sectionPermissions.record
			if (caseSectionPermissions && caseSectionPermissions[permissionType] && caseSectionPermissions[permissionType].length > 0) {
				return caseSectionPermissions[permissionType]
			}
		}

		var permissions = field.permissions
			? this.currentCase!.isCase ? field.permissions.case : field.permissions.record
			: null
		if (permissions) {
			return permissions[permissionType]
		}

		if (field.field.permissions) {
			return (this.currentCase!.isCase ? field.field.permissions.case : field.field.permissions.record)[permissionType]
		}

		return []
	}

	private displayEditTeams (field: CaseField): string {
		const teams = this.getPermissionTeams(field, "edit")
		if (!teams || teams.length === 0) {
			return ""
		}

		return teams.map(team => team.title).join(", ")
	}

	private hasViewPermission (field: CaseField): boolean {
		if (!this.me) return false

		const teams = this.getPermissionTeams(field, "view")
		if (!teams || teams.length === 0) {
			return true
		}

		return this.me.teams && teams.some(team => this.me.teams.find((myTeam: any) => myTeam.id === team.id))
	}

	private hasPermission (field: CaseField): boolean {
		if (!this.canEditCurrentCase) {
			return false
		}
		if (field.field.displayReadOnly) {
			return false
		}

		const teams = this.getPermissionTeams(field, "edit")
		if (!teams || teams.length === 0) {
			return true
		}

		return this.me.teams && teams.some(team => this.me.teams.find((myTeam: any) => myTeam.id === team.id))
	}

	private openNewItemModal (openArgs: any) {
		this.$refs.viewItemDetails.open(openArgs)
	}

	private fieldRequired (field: CaseField) {
		if (!this.currentCase!.id) return false

		if (this.currentCase!.isCase ? !field.requiredCase : !field.requiredRecord) return false

		if (field.field.dataType?.toLowerCase() === "file-only") {
			let id = field.field.id !== undefined ? field.field.id : ""
			if (id === "" || this.fieldFiles(id).length === 0) {
				return true
			} else {
				return false
			}
		} else {
			return CaseFieldValidation.fieldRequired(field)
		}
	}

	private sectionDescription (section: string) {
		if (!this.currentCase || !this.currentCase.sectionDetails || !this.currentCase.sectionDetails[section]) {
			return ""
		}

		return this.currentCase.sectionDetails[section].description
	}

	private setFieldCalculated (fieldInstance: FieldInstance, calculated: boolean) {
		const field = fieldInstance.field
		if (this.currentCase && this.currentCase.id) {
			api.setFieldCalculated(this.currentCase.id, field.id!, calculated)
		} else {
			if (calculated) {
				field.dataType = "calculated"
				fieldInstance.value = null
			} else if (field.dataType === "calculated") {
				const dataType = CalculationDataTypeValue[field.extensions!.calculationType as CalculationType]
				switch (dataType) {
					case DataType.string:
						field.dataType = "text"
						break
					case DataType.dateTime:
						field.dataType = "date"
						break
					case DataType.number:
						field.dataType = "number"
						break
					case DataType.boolean:
						field.dataType = "checkbox"
						break
				}
			}
		}
	}

	private isCheckboxCalculation (field: Field): boolean {
		return !!field.extensions &&
			CalculationDataTypeValue[field.extensions.calculationType as CalculationType] === DataType.boolean
	}

	private uploadFile (fieldAndFiles: { fieldId: string, uploadFiles: any[] }) {
		this.$emit("uploadFiles", fieldAndFiles)
	}

	private updateInProgress (fieldId: string, value: string) {
		if (!this.inProgressEnabled) return

		if (this.postInProgressDebounces[fieldId]) {
			clearTimeout(this.postInProgressDebounces[fieldId])
		}

		this.postInProgressDebounces[fieldId] = setTimeout(async () => {
			Vue.set(this.savingFields, fieldId, true)

			clearTimeout(this.postInProgressDebounces[fieldId])
			delete this.postInProgressDebounces[fieldId]

			await this.hubCaseService.inProgressUpdate(this.currentCase!.id!, fieldId, value)
			Vue.delete(this.savingFields, fieldId)
		}, 1000)
	}
}
